/**
 * Headless CMS layer backed by Shopify Metaobjects.
 *
 * Edit content in Shopify Admin → Content → Metaobjects.
 * Definitions to create (Settings → Custom data → Metaobjects), each with
 * "Storefronts can access" enabled:
 *
 *   - hero_section (singleton)
 *       eyebrow:text, title:rich_text, italic_title:text,
 *       edition_note:text, background_image:file_reference, cta_label:text
 *
 *   - about_page (singleton)
 *       section_number:text, headline:rich_text, italic_headline:text,
 *       short_intro:rich_text, body:rich_text, image:file_reference,
 *       cta_label:text
 *
 *   - story_page (singleton)
 *       section_number:text, headline:rich_text, italic_headline:text,
 *       short_intro:rich_text, body:rich_text,
 *       image_left:file_reference, image_right:file_reference
 *
 *   - spec_highlight (multiple — create 4 entries)
 *       value:text, label:text, order:integer
 *
 *   - image_band (singleton)
 *       image_left:file_reference, image_right:file_reference,
 *       alt_left:text, alt_right:text
 *
 *   - site_settings (singleton)
 *       brand_name:text, footer_tagline:text, ships_copy:text
 *
 * Empty fields fall back to the hardcoded copy / bundled images, so the
 * site never breaks while you set things up.
 */
import { storefrontApiRequest } from "./shopify";

type FieldMap = Record<string, string | null>;

const METAOBJECT_FIELDS = `
  fields {
    key
    value
    reference {
      ... on MediaImage { image { url altText } }
      ... on GenericFile { url }
    }
  }
`;

const CMS_QUERY = `
  query CmsContent {
    hero: metaobjects(type: "hero_section", first: 1) { edges { node { ${METAOBJECT_FIELDS} } } }
    about: metaobjects(type: "about_page", first: 1) { edges { node { ${METAOBJECT_FIELDS} } } }
    story: metaobjects(type: "story_page", first: 1) { edges { node { ${METAOBJECT_FIELDS} } } }
    imageBand: metaobjects(type: "image_band", first: 1) { edges { node { ${METAOBJECT_FIELDS} } } }
    settings: metaobjects(type: "site_settings", first: 1) { edges { node { ${METAOBJECT_FIELDS} } } }
    specs: metaobjects(type: "spec_highlight", first: 12) { edges { node { ${METAOBJECT_FIELDS} } } }
  }
`;

function flatten(node: any): FieldMap {
  if (!node?.fields) return {};
  const out: FieldMap = {};
  for (const f of node.fields) {
    if (f.reference?.image?.url) out[f.key] = f.reference.image.url;
    else if (f.reference?.url) out[f.key] = f.reference.url;
    else out[f.key] = f.value ?? null;
  }
  return out;
}

function firstOrEmpty(group: any): FieldMap {
  return flatten(group?.edges?.[0]?.node);
}

export interface CmsContent {
  hero: FieldMap;
  about: FieldMap;
  story: FieldMap;
  imageBand: FieldMap;
  settings: FieldMap;
  specs: FieldMap[];
}

export async function getCmsContent(): Promise<CmsContent> {
  try {
    const res = await storefrontApiRequest(CMS_QUERY);
    const d = res?.data ?? {};
    return {
      hero: firstOrEmpty(d.hero),
      about: firstOrEmpty(d.about),
      story: firstOrEmpty(d.story),
      imageBand: firstOrEmpty(d.imageBand),
      settings: firstOrEmpty(d.settings),
      specs: (d.specs?.edges ?? [])
        .map((e: any) => flatten(e.node))
        .sort((a: FieldMap, b: FieldMap) => Number(a.order ?? 0) - Number(b.order ?? 0)),
    };
  } catch (err) {
    // Metaobject types may not exist yet — return empty so fallbacks render.
    console.warn("[cms] getCmsContent failed, using fallbacks:", err);
    return { hero: {}, about: {}, story: {}, imageBand: {}, settings: {}, specs: [] };
  }
}

/** Returns the field value or the fallback if empty / missing. */
export function field(map: FieldMap | undefined, key: string, fallback: string): string {
  const v = map?.[key];
  return v && v.trim().length > 0 ? v : fallback;
}

/**
 * Shopify rich_text stores a JSON AST. Render to plain text with paragraph
 * breaks. For richer rendering we'd parse to JSX; plain text is fine for now.
 */
export function richText(value: string | null | undefined, fallback: string): string {
  if (!value) return fallback;
  try {
    const parsed = JSON.parse(value);
    const walk = (n: any): string => {
      if (!n) return "";
      if (typeof n === "string") return n;
      if (Array.isArray(n)) return n.map(walk).join("");
      if (n.type === "text") return n.value ?? "";
      if (n.type === "paragraph") return (n.children ?? []).map(walk).join("") + "\n\n";
      return (n.children ?? []).map(walk).join("");
    };
    const out = walk(parsed).trim();
    return out.length > 0 ? out : fallback;
  } catch {
    return value;
  }
}

export const cmsQueryOptions = {
  queryKey: ["cms-content"] as const,
  queryFn: getCmsContent,
  staleTime: 60_000,
};
